<script>
import { mapGetters, mapMutations } from 'vuex'

// Page owns the /location?lat=..&lng=.. route. It parses the URL, reverse-geocodes
// the coordinates, and commits the result to the `locationRequest` bus.
// GeofleetMap (the persistent parent) watches the bus and updates its search
// bar + map view. This keeps route logic on the component that owns the route.
// No DOM output — render() returns null since the side effects live in
// created()/watchers/beforeDestroy.
export default {
    name: 'LocationPage',
    render() {
        return null
    },
    computed: {
        ...mapGetters({ locationRequest: 'getLocationRequest' })
    },
    created() {
        this.applyRoute()
    },
    watch: {
        // Browser back/forward between two /location URLs reuses this component
        // and only changes the query — react via query watchers.
        '$route.query.lat': 'applyRoute',
        '$route.query.lng': 'applyRoute'
    },
    beforeDestroy() {
        // Bus matches the page lifecycle — clear it when leaving the route.
        this.setLocationRequest(null)
    },
    methods: {
        ...mapMutations(['setLocationRequest']),
        parseLocationParams() {
            const lat = parseFloat(this.$route.query.lat)
            const lng = parseFloat(this.$route.query.lng)
            if (!Number.isFinite(lng) || !Number.isFinite(lat)) {
                return null
            }
            return { lat, lng }
        },
        async resolveAddressLabel(lat, lng) {
            try {
                const address = await this.$reverseGeocoding({ lat, lng, full: true })
                if (address) {
                    return address
                }
            } catch {
                // fall through to coordinate fallback
            }
            return `${lat}, ${lng}`
        },
        isStillOnSameRoute(lat, lng) {
            return this.$route.name === 'geofleet.location'
                && parseFloat(this.$route.query.lat) === lat
                && parseFloat(this.$route.query.lng) === lng
        },
        async applyRoute() {
            const coords = this.parseLocationParams()
            if (!coords) {
                this.$snotify.error(this.__('invalid_coordinates'), { position: 'centerTop' })
                this.$router.replace({ name: 'geofleet.index' })
                return
            }

            // Skip the geocode round-trip if the bus already matches — happens
            // when the user picked from the search bar and GeofleetMap committed
            // the request before pushing the URL here.
            const onBus = this.locationRequest
            const alreadyOnBus = onBus && onBus.lat === coords.lat && onBus.lng === coords.lng
            if (alreadyOnBus) {
                return
            }

            const label = await this.resolveAddressLabel(coords.lat, coords.lng)

            // Drop the result if the URL changed while we were awaiting the geocode.
            if (!this.isStillOnSameRoute(coords.lat, coords.lng)) {
                return
            }

            this.setLocationRequest({ lat: coords.lat, lng: coords.lng, label })
        }
    }
}
</script>
